/**
 * Admin -> Edge Fleet -> Install tokens
 *
 * Command Plane Phase 3 step 4. Until this existed there was no way to see a
 * token at all: the only surface was minting one. For a single token that is
 * survivable - it binds to one pod and you can see the pod. For a FLEET token
 * it is not, because the thing you need to know is a number that lives only on
 * the token: how much of the ceiling is spent. Without it an operator learns
 * the fleet is full when devices start failing to enrol.
 *
 * TWO NUMBERS, NOT ONE. `enrolledCount` is what the token has SPENT and only
 * ever goes up - recycling slots on decommission would let a fleet quietly
 * exceed its licence. `livePods` is what is running now. They diverge as soon
 * as a device is retired, and an operator reading one as the other will
 * mis-size the next rollout, so both are shown with their own labels.
 *
 * REVOKE AND SEAL ARE DELIBERATELY NOT THE SAME BUTTON. Revoke stops every
 * device on the token; seal stops new ones joining and leaves the running fleet
 * alone. On a hundred machines that difference is the whole ballgame, so revoke
 * requires a typed reason and states the number of devices it is about to stop
 * BEFORE it happens.
 *
 * Release 2.1: the business unit every device on the token is placed in, and
 * Extend (a longer enrolment window, a higher device limit) for a weeks-long
 * rollout. Colours are the theme's semantic tokens, so both themes read.
 *
 * Release 2.1 item 24: a token is a WAVE. Each wave gets a card (enrolled,
 * running, reported in the last 15 minutes, scanned OK in the last 24 hours,
 * failing - one grouped query on the server), a Pause / Resume that reaches
 * every device on the wave at its next check-in, and Retire silent devices,
 * which retires the devices on the wave that stopped checking in N days ago,
 * after showing how many that is.
 *
 * Backend:
 *   GET   /v1/edge/install-tokens
 *   POST  /v1/edge/install-tokens/:id/revoke   { reason }
 *   POST  /v1/edge/install-tokens/:id/seal
 *   PATCH /v1/edge/install-tokens/:id          { ttlSeconds?, maxDevices?, reason }
 *   GET   /v1/edge/install-tokens/waves
 *   POST  /v1/edge/rollout/scope-overrides       { scopeType: 'install_token', scopeId, paused, reason }
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { CalendarPlus, KeyRound, Loader2, Lock, Pause, Play, RefreshCw, UserX, XCircle } from 'lucide-react';

import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  listBusinessUnits,
  listEdgeInstallTokens,
  listEdgeWaves,
  revokeEdgeInstallToken,
  sealEdgeInstallToken,
  setEdgeScopePause,
  type EdgeInstallTokenRow,
  type EdgeWaveHealth,
} from '@/lib/api';
import { InstallTokenExtendForm } from './InstallTokenExtendForm';
import { SilentDevicesForm } from './SilentDevicesForm';

/**
 * One wave at a glance. Five numbers, each with its own label: "running" and
 * "reported in 15 min" diverge when devices sleep or leave the network, and
 * "failing" is the number that says stop the next wave.
 */
function WaveCard({
  token,
  wave,
  unit,
}: {
  token: EdgeInstallTokenRow;
  wave: EdgeWaveHealth;
  unit: string;
}) {
  const stats: Array<{ label: string; value: number; tone?: string }> = [
    { label: 'Enrolled', value: wave.enrolled },
    { label: 'Running', value: wave.live },
    { label: 'Reported in 15 min', value: wave.reportedLast15m },
    { label: 'Scanned OK in 24 h', value: wave.scannedOkLast24h },
    { label: 'Failing', value: wave.failing, tone: wave.failing > 0 ? 'text-destructive' : undefined },
  ];
  return (
    <div className="rounded-md border border-border/40 bg-background/40 p-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="font-mono text-xs text-foreground">Wave {token.id.slice(0, 8)}</span>
        {wave.paused ? (
          <span className="rounded border border-warning/30 bg-warning/15 px-2 py-0.5 text-xs text-warning">
            Paused
          </span>
        ) : null}
      </div>
      <p className="mb-2 truncate text-xs text-muted-foreground" title={unit}>
        {unit}
      </p>
      <dl className="grid grid-cols-5 gap-2">
        {stats.map((s) => (
          <div key={s.label}>
            <dt className="text-[11px] leading-tight text-muted-foreground">{s.label}</dt>
            <dd className={`text-base font-semibold tabular-nums ${s.tone ?? 'text-foreground'}`}>{s.value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

/**
 * A token is shut in three different ways and an operator needs to tell them
 * apart: revoked is deliberate, expired is the clock, sealed is full on
 * purpose. Rendering them all as one grey "inactive" chip is how a revoked
 * token gets mistaken for a lapsed one.
 */
function tokenState(t: EdgeInstallTokenRow): { label: string; tone: string; hint: string } {
  if (t.revokedAt) {
    return {
      label: 'Revoked',
      tone: 'bg-destructive/15 text-destructive border-destructive/30',
      hint: t.revocationReason ?? 'revoked',
    };
  }
  if (new Date(t.expiresAt).getTime() < Date.now()) {
    return {
      label: 'Expired',
      tone: 'bg-warning/15 text-warning border-warning/30',
      // Not a dead token. Expiry gates ENROLMENT, not operation - devices
      // already enrolled keep running on it by design.
      hint: 'no new devices; enrolled devices keep running',
    };
  }
  if (t.kind === 'fleet' && t.maxDevices !== null && t.enrolledCount >= t.maxDevices) {
    return {
      label: 'Full',
      tone: 'bg-muted text-muted-foreground border-border',
      hint: 'limit reached; the next device will be refused',
    };
  }
  return {
    label: 'Active',
    tone: 'bg-success/15 text-success border-success/30',
    hint: `devices can join until ${new Date(t.expiresAt).toLocaleDateString()}`,
  };
}

export function InstallTokenPanel() {
  const [tokens, setTokens] = useState<EdgeInstallTokenRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [extendingId, setExtendingId] = useState<string | null>(null);
  const [retiringId, setRetiringId] = useState<string | null>(null);
  const [unitNames, setUnitNames] = useState<Map<string, string>>(new Map());
  const [waves, setWaves] = useState<Map<string, EdgeWaveHealth>>(new Map());
  const [waveError, setWaveError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const r = await listEdgeInstallTokens();
      setTokens(r.tokens);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'could not load install tokens');
    }
    // The wave numbers are a separate read; if it fails the tokens still show
    // and the failure is said out loud rather than showing zeros.
    try {
      const w = await listEdgeWaves();
      setWaves(new Map(w.waves.map((x) => [x.tokenId, x])));
      setWaveError(null);
    } catch (e) {
      setWaveError(e instanceof Error ? e.message : 'could not load wave health');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Unit names for the Business unit column. A failure leaves the ids showing
  // rather than hiding the column: the unit is the fact that matters.
  useEffect(() => {
    let cancelled = false;
    listBusinessUnits()
      .then((r) => {
        if (!cancelled) setUnitNames(new Map(r.businessUnits.map((b) => [b.id, b.name])));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  // One card per wave that has enrolled anything and is not revoked.
  const waveCards = useMemo(
    () =>
      (tokens ?? []).flatMap((t) => {
        const w = waves.get(t.id);
        return t.revokedAt === null && w && w.enrolled > 0 ? [{ token: t, wave: w }] : [];
      }),
    [tokens, waves],
  );

  const unitLabel = useMemo(
    () => (id: string | null) =>
      id === null ? 'None (visible to every unit)' : (unitNames.get(id) ?? id.slice(0, 8)),
    [unitNames],
  );

  const onRevoke = async (t: EdgeInstallTokenRow) => {
    // The count comes from the row the operator is looking at, and it is in the
    // prompt itself. "Revoke this token?" does not convey that 100 laptops stop.
    const what =
      t.livePods === 0
        ? 'No devices are currently running on this token.'
        : `This will STOP ${t.livePods} running device${t.livePods === 1 ? '' : 's'} at their next heartbeat.`;
    const reason = window.prompt(`${what}\n\nReason (recorded in the audit chain):`);
    if (reason === null) return; // cancelled
    if (reason.trim().length < 3) {
      setNotice('Revoke needs a reason of at least 3 characters.');
      return;
    }
    setBusyId(t.id);
    setNotice(null);
    try {
      const r = await revokeEdgeInstallToken(t.id, reason.trim());
      setNotice(
        r.livePodsStopped.length > 0
          ? `Revoked. Stopped: ${r.livePodsStopped.join(', ')}`
          : 'Revoked. No running devices were affected.',
      );
      await load();
    } catch (e) {
      setNotice(e instanceof Error ? e.message : 'revoke failed');
    } finally {
      setBusyId(null);
    }
  };

  const onPauseToggle = async (t: EdgeInstallTokenRow, pause: boolean) => {
    const what = pause
      ? `Pause wave ${t.id.slice(0, 8)}: ${t.livePods} running device${t.livePods === 1 ? '' : 's'} stop scanning at their next check-in (within about 10 minutes). Nothing is uninstalled.`
      : `Resume wave ${t.id.slice(0, 8)}: its devices scan again at their next check-in, unless a business unit or fleet pause, or a device's own pause, still holds them.`;
    const reason = window.prompt(`${what}\n\nReason (recorded in the audit chain):`);
    if (reason === null) return;
    if (reason.trim().length < 3) {
      setNotice(`${pause ? 'Pause' : 'Resume'} needs a reason of at least 3 characters.`);
      return;
    }
    setBusyId(t.id);
    setNotice(null);
    try {
      await setEdgeScopePause({ scopeType: 'install_token', scopeId: t.id, paused: pause, reason: reason.trim() });
      setNotice(pause ? `Wave ${t.id.slice(0, 8)} paused.` : `Wave ${t.id.slice(0, 8)} resumed.`);
      await load();
    } catch (e) {
      setNotice(e instanceof Error ? e.message : 'could not change the pause');
    } finally {
      setBusyId(null);
    }
  };

  const onSeal = async (t: EdgeInstallTokenRow) => {
    setBusyId(t.id);
    setNotice(null);
    try {
      const r = await sealEdgeInstallToken(t.id);
      setNotice(
        r.sealed
          ? `Sealed at ${r.atDevices} device${r.atDevices === 1 ? '' : 's'}. Running devices are unaffected.`
          : 'Could not seal this token.',
      );
      await load();
    } catch (e) {
      // The 409 body explains WHY (an unused fleet token cannot be sealed -
      // that is just revoking it). Showing it beats a generic failure.
      setNotice(e instanceof Error ? e.message : 'seal failed');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <GlassCard className="p-0">
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle className="flex items-center gap-2">
            <KeyRound className="h-4 w-4" />
            Install tokens
          </CardTitle>
          <CardDescription>
            A fleet token enrols many devices from one package; each token is one wave. Extend
            keeps the same package working for the next wave. Pause stops a wave scanning and
            Resume starts it again. Seal stops new devices joining; revoke stops the ones already
            running.
          </CardDescription>
        </div>
        <Button variant="ghost" size="sm" onClick={() => void load()} aria-label="Reload tokens">
          <RefreshCw className="h-4 w-4" />
        </Button>
      </CardHeader>

      <div className="px-6 pb-6">
        {error ? (
          <p className="text-sm text-destructive">{error}</p>
        ) : tokens === null ? (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading tokens...
          </p>
        ) : tokens.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No install tokens yet. Mint one from Edge provisioning.
          </p>
        ) : (
          <>
          {waveError ? (
            <p className="mb-3 text-sm text-destructive">Wave health: {waveError}</p>
          ) : null}
          {waveCards.length > 0 ? (
            <div className="mb-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {waveCards.map(({ token: t, wave: w }) => (
                <WaveCard key={t.id} token={t} wave={w} unit={unitLabel(t.businessUnitId)} />
              ))}
            </div>
          ) : null}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="py-2 pr-4">Token</th>
                  <th className="py-2 pr-4">Type</th>
                  <th className="py-2 pr-4">Business unit</th>
                  <th className="py-2 pr-4">Enrolled</th>
                  <th className="py-2 pr-4">Running now</th>
                  <th className="py-2 pr-4">State</th>
                  <th className="py-2 pr-4" />
                </tr>
              </thead>
              <tbody>
                {tokens.map((t) => {
                  const st = tokenState(t);
                  const shut = t.revokedAt !== null;
                  const paused = waves.get(t.id)?.paused === true;
                  return [
                    <tr key={t.id} className="border-t border-border/40">
                      {/* Only the id. The token itself is shown once at mint and
                          never again - not even its hash leaves the server. */}
                      <td className="py-2 pr-4 font-mono text-xs text-foreground">
                        {t.id.slice(0, 8)}
                      </td>
                      <td className="py-2 pr-4">
                        <Badge variant="outline">{t.kind}</Badge>
                      </td>
                      <td className="py-2 pr-4 text-xs text-foreground">{unitLabel(t.businessUnitId)}</td>
                      <td className="py-2 pr-4 tabular-nums">
                        {t.kind === 'fleet' && t.maxDevices !== null
                          ? `${t.enrolledCount} / ${t.maxDevices}`
                          : t.enrolledCount > 0 || t.boundPodId
                            ? '1 / 1'
                            : '0 / 1'}
                      </td>
                      <td className="py-2 pr-4 tabular-nums text-foreground">{t.livePods}</td>
                      <td className="py-2 pr-4">
                        <span className={`rounded border px-2 py-0.5 text-xs ${st.tone}`}>
                          {st.label}
                        </span>
                        {paused ? (
                          <span className="ml-2 rounded border border-warning/30 bg-warning/15 px-2 py-0.5 text-xs text-warning">
                            Paused
                          </span>
                        ) : null}
                        <span className="ml-2 text-xs text-muted-foreground">{st.hint}</span>
                      </td>
                      <td className="py-2 pr-4 text-right">
                        {shut ? null : (
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={busyId === t.id}
                              onClick={() => void onPauseToggle(t, !paused)}
                              data-action="disc.adm_edge_fleet.a10"
                            >
                              {paused ? (
                                <>
                                  <Play className="mr-1 h-3 w-3" /> Resume
                                </>
                              ) : (
                                <>
                                  <Pause className="mr-1 h-3 w-3" /> Pause
                                </>
                              )}
                            </Button>
                            {t.livePods > 0 ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                disabled={busyId === t.id}
                                onClick={() => {
                                  setExtendingId(null);
                                  setRetiringId(retiringId === t.id ? null : t.id);
                                }}
                              >
                                <UserX className="mr-1 h-3 w-3" /> Retire silent
                              </Button>
                            ) : null}
                            {t.kind === 'fleet' ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                disabled={busyId === t.id}
                                onClick={() => {
                                  setRetiringId(null);
                                  setExtendingId(extendingId === t.id ? null : t.id);
                                }}
                              >
                                <CalendarPlus className="mr-1 h-3 w-3" /> Extend
                              </Button>
                            ) : null}
                            {t.kind === 'fleet' && t.enrolledCount > 0 ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                disabled={busyId === t.id}
                                onClick={() => void onSeal(t)}
                                data-action="disc.adm_edge_fleet.a11"
                              >
                                <Lock className="mr-1 h-3 w-3" /> Seal
                              </Button>
                            ) : null}
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-destructive hover:text-destructive"
                              disabled={busyId === t.id}
                              onClick={() => void onRevoke(t)}
                              data-action="disc.adm_edge_fleet.a09"
                            >
                              <XCircle className="mr-1 h-3 w-3" /> Revoke
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>,
                    extendingId === t.id ? (
                      <tr key={`${t.id}-extend`}>
                        <td colSpan={7} className="pb-3">
                          <InstallTokenExtendForm
                            token={t}
                            onCancel={() => setExtendingId(null)}
                            onDone={(message) => {
                              setExtendingId(null);
                              setNotice(message);
                              void load();
                            }}
                          />
                        </td>
                      </tr>
                    ) : null,
                    retiringId === t.id ? (
                      <tr key={`${t.id}-retire`}>
                        <td colSpan={7} className="pb-3">
                          <SilentDevicesForm
                            token={t}
                            onCancel={() => setRetiringId(null)}
                            onDone={(message) => {
                              setRetiringId(null);
                              setNotice(message);
                              void load();
                            }}
                          />
                        </td>
                      </tr>
                    ) : null,
                  ];
                })}
              </tbody>
            </table>
          </div>
          </>
        )}
        {notice ? <p className="mt-3 text-sm text-foreground">{notice}</p> : null}
      </div>
    </GlassCard>
  );
}
